/**
 * LoadingState Component
 * 
 * Reusable loading state with spinner and optional message.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

export interface LoadingStateProps {
  /** Loading message */
  message?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Additional className */
  className?: string;
  /** Spinner color */
  color?: string;
}

const sizeClasses = {
  sm: 'py-4',
  md: 'py-8',
  lg: 'py-12',
  full: 'min-h-[400px]',
};

const spinnerSizes = {
  sm: 20,
  md: 28,
  lg: 36,
  full: 48,
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  className = '',
  color = 'text-primary-500',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center ${sizeClasses[size]} ${className}`}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        className={color}
      >
        <Loader2 size={spinnerSizes[size]} />
      </motion.div>
      {message && (
        <p className="mt-3 text-sm text-gray-500">{message}</p>
      )}
    </div>
  );
};

/**
 * LoadingSpinner - Simple inline spinner
 */
export const LoadingSpinner: React.FC<{ size?: number; className?: string }> = ({
  size = 20,
  className = 'text-primary-500',
}) => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    className={`inline-flex ${className}`}
  >
    <Loader2 size={size} />
  </motion.div>
);

export default LoadingState;


